import { Option } from 'fp-ts/Option';
import { LabelKeyObject } from 'react-csv/components/CommonPropTypes';
import { MessageMedium } from 'constants/messageMediums';
import { BrandAffinityLevel } from 'modules/domains';
import { Campaign } from 'modules/campaignIndex/types/Campaign';
import { TimespanEnum } from 'modules/intelligentInsights/types';

/**
  Graphql types
 */

export type Projectid = string;
export type RunId = string;
export type Timespan = TimespanEnum;
export type ComputedDate = string;
export type Count = number;
export type History = HistoryItem[];
export enum DeltaDirectionEnum {
  gain = 'gain',
  decline = 'decline',
  unscored = 'unscored',
}
export type Delta = number;
export type Deltas = DeltaItem[];
export type TransitionMap = TransitionItem[];
export type SupportedMediums =
  | MessageMedium.Email
  | MessageMedium.SMS
  | MessageMedium.Push
  | MessageMedium.InApp;
export enum EngagementMetricEnum {
  Sends = 'sends',
  Opens = 'opens',
  Clicks = 'clicks',
}
export type EngagementMetric = EngagementMetricEnum;
export type Metrics1 = EngagementMetricItem[];
export type Metrics = MediumEngagementMetrics[];
export type EngagementMetrics = EngagementMetricsItem[];
export enum DirectionEnum {
  up = 'up',
  down = 'down',
  unchanged = 'unchanged',
}
export type Direction = DirectionEnum;
export type CampaignId = number;
export type Impacts = ChannelCampaignImpact[];
export type CampaignImpact = CampaignImpactItemWrapper[];
export type TopCampaigns = TopCampaign[];

export type AllUnscoredMetrics = UnscoredMetrics[];
export type TotalSends = number;
export type TotalSendSkips = number;
export type DeliveryRate = number;
export type BounceRate = number;
export type UniqueUsersUnsubscribed = number;

export enum MessagingType {
  MessageType = 'Message type',
  Channel = 'Channel',
}
export type UnsubMessage = {
  id: number;
  score: number;
  type?: MessagingType.Channel | MessagingType.MessageType;
};
export type UnsubMessageEnhanced = UnsubMessage & {
  name: string;
  type: MessagingType.Channel | MessagingType.MessageType;
};
export type TopUnsubMessageTypeIds = Array<UnsubMessage>;
export type TopUnsubChannelIds = Array<UnsubMessage>;

export interface BrandAffinityInsightResponse {
  brandAffinityReporting: BrandAffinityInsightData;
}

export interface BrandAffinityInsightData {
  /*
   Known properties
  */
  projectId: Projectid;
  /**
    @description User affinity trends graph
  */
  history: History;
  /**
  @description User affinity trends largest deltas
  */
  deltas: Deltas;
  /**
  @description User affinity user movement
  */
  transitionMap: TransitionMap;
  /**
  @description User affinity engagement overview
  */
  engagementMetrics: EngagementMetrics;
  /**
   @description Top Contributing campaigns and Contribution by channel
  */
  campaignImpacts: CampaignImpact;
  /**
    @description Unscored metrics
   */
  unscoredMetrics: AllUnscoredMetrics;
}

export interface HistoryItem {
  computedForDate: ComputedDate;
  labelCounts: HistoryLabel[];
}
export interface HistoryLabel {
  brandAffinityLabel: BrandAffinityLevel;
  count: Count;
  percent: number;
}

export interface DeltaItem {
  brandAffinityLabel: BrandAffinityLevel;
  delta: Delta;
  percent: Delta;
}

export interface TransitionItem {
  count: Count;
  endBrandAffinityLabel: BrandAffinityWithDeleted;
  startBrandAffinityLabel: BrandAffinityWithNew;
}
export interface EngagementMetricsItem {
  brandAffinityLabel: BrandAffinityLevel;
  metrics: Metrics;
}
export interface MediumEngagementMetrics {
  messageMedium: SupportedMediums;
  metrics: Metrics1;
}
export interface EngagementMetricItem {
  count: Count;
  metricLabel: EngagementMetricEnum;
}
export interface CampaignImpactItemWrapper {
  brandAffinityLabel: BrandAffinityLevel;
  campaignCount: Impacts;
  topCampaignsUpwards: TopCampaigns;
  topCampaignsDownwards: TopCampaigns;
}
export interface ChannelCampaignImpact {
  count: Count;
  direction: DirectionEnum;
  messageMedium: SupportedMediums;
}
export interface DeprecatedCampaignTopItem {
  campaignId: CampaignId;
  direction: DirectionEnum;
}

export interface TopCampaign {
  campaignId: CampaignId;
  messageMedium: SupportedMediums;
  score: number;
}

export interface UnscoredMetrics {
  totalSendSkips: TotalSendSkips;
  uniqueUsersUnsub: UniqueUsersUnsubscribed;
  topUnsubMessageTypeIds: TopUnsubMessageTypeIds;
  topUnsubChannelIds: TopUnsubChannelIds;
  timespan?: Timespan;
}

/**
  Context types
 */
export interface BrandAffinityInsightsCoreContext {
  fetchCoreData: (nextTimespan: Timespan) => void;
  insightsData: BrandAffinityInsightData;
  isError: boolean;
  isLoading: boolean;
  isReady: boolean;
  selectedAffinityTab: BrandAffinityLevel;
  setSelectedAffinityTab: (tabValueToSet: BrandAffinityLevel) => void;
}

export type BrandAffinityInsightsCoreContextVars = {
  timespan: Timespan;
  startDate: string;
  endDate: string;
};

export type GraphHistory = {
  categories: string[];
  byCount: {
    historyPoints: Array<Count[]>;
  };
  byPercent: {
    historyPoints: Array<Count[]>;
  };
};

export type CalculatedDeltas = {
  byCount: {
    [DeltaDirectionEnum.gain]: DeltaItem;
    [DeltaDirectionEnum.decline]: DeltaItem;
    [DeltaDirectionEnum.unscored]: DeltaItem;
  };
  byPercent: {
    [DeltaDirectionEnum.gain]: DeltaItem;
    [DeltaDirectionEnum.decline]: DeltaItem;
    [DeltaDirectionEnum.unscored]: DeltaItem;
  };
};

export type EngagementOverview = {
  [key in EngagementMetricEnum]: number;
};

export type EngagementUsersMetrics = {
  history: {
    count: number;
    percent: number;
  };
  delta: {
    count: number;
    percent: number;
  };
};
export type CalculatedUsersMetrics = {
  [levelKey in BrandAffinityLevel]: EngagementUsersMetrics;
};

export type CalculatedEngagementMetrics = {
  [levelKey in BrandAffinityLevel]: {
    overview: EngagementOverview;
  };
};

export type CalculatedUnscoredMetrics = UnscoredMetrics & {
  totalSends: TotalSends;
};

export type CsvData = { [key: string]: string | number };
export type Header = LabelKeyObject;

export type CampaignImpactChartData = {
  chartData: Array<{ name: DirectionEnum; data: number[] }>;
  mediums: Array<string | null>;
};
export type CalculatedTopCampaign = TopCampaign & { direction: DirectionEnum };

export type CampaignChartDataCreator = (
  label: CampaignImpactAffinityLevels,
) => CampaignImpactChartData;
export type TopCampaignsDataCreator = (label: CampaignImpactAffinityLevels) => {
  all: Array<CalculatedTopCampaign>;
  down: Array<CalculatedTopCampaign>;
  up: Array<CalculatedTopCampaign>;
};

export type CampaignImpactDataCreators = {
  getCampaignChartData: CampaignChartDataCreator;
  getSortedCampaigns: TopCampaignsDataCreator;
};

export interface BrandAffinityInsightsDataManagementContext {
  managedInsightsData: {
    history: Option<GraphHistory>;
    deltas: Option<CalculatedDeltas>;
    usersMetrics: Option<CalculatedUsersMetrics>;
    engagementMetrics: Option<CalculatedEngagementMetrics>;
    campaignImpact: Option<CampaignImpactDataCreators>;
    unscoredMetrics: Option<CalculatedUnscoredMetrics>;
    calculatedMovementMetrics: Option<MovementByCohortMetrics[]>;
  };
  timeSpan: {
    selectedTimespan: Timespan;
    updateTimespan: (nextTimespan: Timespan) => void;
  };
  getHistoryCSVData: (trendType: TrendTypeEnum) => {
    csvData: Array<CsvData>;
    headers: Array<Header>;
  };
  affinityBreakdownCSVData: {
    engagementData: {
      csvData: Array<CsvData>;
      headers: Array<Header>;
    };
    movementData: {
      csvData: Array<CsvData>;
      headers: Array<Header>;
    };
    campaigns: {
      csvData: Array<CsvData>;
      headers: Array<Header>;
    };
  };
}

/**
 Client domain types
 */
export enum TrendTypeEnum {
  Count = 'Count',
  Percent = 'Percent',
}

export type TimeSpanOption = {
  label: string;
  value: string;
};

export type UsersByCohort = Partial<{
  [key in BrandAffinityLevel]: number;
}>;

export interface MovementByCohortMetrics {
  cohort: BrandAffinityLevel;
  deletedUsers: number;
  newUsersCohort: UsersByCohort;
  newUsersToProject: number;
  totalNewUsers: number;
  totalRetainedUsers: number;
  totalUsersLeft: number;
  usersLeftCohort: UsersByCohort;
}

export enum DirectionalityFilterEnum {
  UP = DirectionEnum.up,
  DOWN = DirectionEnum.down,
  ALLMOVEMENT = 'allMovement',
}

export type ContributionCampaign = Pick<
  Campaign,
  'id' | 'messageMedium' | 'name' | 'templateId' | 'campaignTypeDefinition'
>;

export type CampaignWithDirectionality = ContributionCampaign & {
  direction: DirectionEnum;
};

export const SupportedMediums = [
  MessageMedium.Email,
  MessageMedium.InApp,
  MessageMedium.Push,
  MessageMedium.SMS,
];

export type CampaignImpactAffinityLevels = Exclude<
  (typeof BrandAffinityLevel)[keyof typeof BrandAffinityLevel],
  BrandAffinityLevel.UNSCORED
>;
export type BrandAffinityLevels = (typeof BrandAffinityLevel)[keyof typeof BrandAffinityLevel];

export const BrandAffinityLevelNewUser = 'new';
export const BrandAffinityLevelDeletedUser = 'deleted';

export type BrandAffinityWithNew = BrandAffinityLevels | typeof BrandAffinityLevelNewUser;
export type BrandAffinityWithDeleted = BrandAffinityLevels | typeof BrandAffinityLevelDeletedUser;
export type BrandAffinityWithNewOrDeleted = BrandAffinityWithNew | BrandAffinityWithDeleted;
